import { useMutation, useQuery } from "@apollo/client";
import { Box, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  ReferenceDot,
  ReferenceLine,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import {
  ANALYZE_PRICE,
  GET_PRICING_COLORS,
  tokenOptions,
} from "../../backend/graphql";
import {
  byColorSystemThenName,
  formatColorQuery,
  formatPricePerCarat,
  pricingColorQuery,
} from "../../tools";
import ColorSpread, {
  prepareColorSpreads,
  COLOR_SYSTEM_LABELS,
} from "../widgets/ColorSpread";
import EquivalenceAlert from "../widgets/EquivalenceAlert";
import SectionPaper from "../widgets/SectionPaper";
import SectionTitle from "../widgets/SectionTitle";
import { toggleSelectorSx } from "../widgets/toggleSelectorSx";
import PageTitle from "../widgets/PageTitle";
import useDocumentTitle from "../widgets/useDocumentTitle";

const colors = [
  "#118C67ff",
  "#2A9776ff",
  "#43A286ff",
  "#5CAE95ff",
  "#75B9A4ff",
  "#8DC4B4ff",
  "#A6CFC3ff",
  "#BFDBD2ff",
  "#D8E6E2ff",
  "#F1F1F1ff",
];

// The diamond field holding the stone's raw value in each color system.
const COLOR_FIELD_BY_SYSTEM: Record<string, string> = {
  GIA: "color",
  Langerman: "langermanColor",
  Flat: "flat",
  Melee: "melee",
};

// A scheme is queried for one color but priced from that color and its confirmed equivalents, so its
// stones are not all the color the graph is labelled with. Given the scheme's equivalent colors,
// return the stone's own color in the scheme's system when it is not rigorously the queried one,
// else null.
// Computed here rather than served as a flag: only this graph needs it, and it is meaningless
// without the system it was decided against.
function equivalentColorOf(diamond: any, equivalentColors: any): string | null {
  if (!equivalentColors || !diamond) return null;
  const rawValue = diamond[COLOR_FIELD_BY_SYSTEM[equivalentColors.system]];
  if (rawValue === null || rawValue === undefined) return null;
  // Both sides raw, so the comparison never depends on how either is spelled.
  if (`${rawValue}` === equivalentColors.color) return null;
  return formatColorQuery(equivalentColors.system, rawValue);
}

// The legend's swatch for equivalent-color stones, drawn to match the marker on the plot rather
// than approximating it: same hollow ring, same stroke-to-radius ratio as renderDiamondDot. Recharts
// renders legend icons into a 32x32 viewBox and clones its own payload entry onto the element, so
// this is a component, which ignores everything it is handed beyond the stroke.
const HollowDotLegendIcon = ({ stroke }: { stroke: string }) => (
  <circle cx={16} cy={16} r={12} fill="none" stroke={stroke} strokeWidth={6} />
);

function reshapeArray(
  arrayA: number[],
  numRows: number,
  numCols: number,
  numPercentiles: 99,
) {
  let arrayB = [];
  for (let i = 0; i < numRows; i++) {
    let arrayC = [];
    for (let j = 0; j < numCols; j++) {
      let start = i * numCols * numPercentiles + j * numPercentiles;
      let end = start + numPercentiles;
      arrayC.push(arrayA.slice(start, end));
    }
    arrayB.push(arrayC);
  }
  return arrayB.reverse();
}

export function SheetGraphPercentile({
  show,
  isNormalized,
  withSamples,
  data,
  width,
  height,
  selectedClarity,
  refPoint,
}: {
  refPoint: any;
  show: any;
  isNormalized: boolean;
  withSamples: boolean;
  data: any;
  width: number;
  height: number;
  selectedClarity: string | null;
}) {
  const rangeMin = 0;
  const [rangeMax, setRangeMax] = useHistoryState("rangeMax", 3);
  const navigate = useNavigate();
  // We keep the data.prices whose color is in the show parameter
  const sheets = data.prices.filter((item: any) =>
    show.map((s: any) => s.value).includes(pricingColorQuery(item)),
  );
  const [shift, setShift] = useState(0);
  const [factor, setFactor] = useState(1);
  const [activeCoord, setActiveCoord] = useState(0);
  const [activePoint, setActivePoint] = useState<any | null>(null);
  const [visibleTooltip, setVisibleTooltip] = useState(false);
  let lastIndex = data.buckets.findIndex((b: number) => b >= rangeMax);
  if (lastIndex === -1) lastIndex = data.buckets.length;

  if (sheets.length === 0) {
    return null;
  }
  const sheet = reshapeArray(sheets[0].values, 11, 20, 99);
  // The equivalent colors of the color this graph is plotted for — null for a scheme with no
  // color system behind it, in which case no stone counts as an equivalent.
  const schemeEquivalence = sheets[0].equivalentColors;

  const percentileDeltas = [1, 5, 10, 15, 20, 25, 30, 35, 40, 45];

  const selectedClarityNum = data.clarities.indexOf(selectedClarity);

  const dataPlot = sheet[selectedClarityNum]
    .map((value: number[], j: number) => ({
      x: data.buckets[j],
      y: value && value[75],
      bucketNum: j,
      percentiles: value,
    }))
    .filter((value: any) => value.bucketNum <= lastIndex);

  //We look for the two dataPlots points whose x value is surrounding the activeCoord, and we interpolate the percentiles
  const percentileInterpolation = (dataPlot: any, activeCoord: number) => {
    let idx = dataPlot.findIndex((value: any) => value.x > activeCoord);
    if (idx === -1) idx = dataPlot.length - 1;
    if (idx === 0) idx = 1;
    const x0 = dataPlot[idx - 1].x;
    const x1 = dataPlot[idx].x;
    const y0 = dataPlot[idx - 1].percentiles;
    const y1 = dataPlot[idx].percentiles;
    const x = activeCoord;
    const y = y0.map(
      (value: number, i: number) =>
        y0[i] + ((y1[i] - y0[i]) * (x - x0)) / (x1 - x0),
    );
    return y;
  };

  const activePercentiles = percentileInterpolation(dataPlot, activeCoord);

  const renderTooltip = (props: any) => {
    if (activeCoord <= 0) return null;
    return (
      <div>
        <Box
          sx={{
            backgroundColor: "#EEE",
            border: 1,
            padding: "5px",
            margin: "3px",
          }}
        >
          <div>
            {selectedClarity}, {activeCoord}&nbsp; Carats
          </div>
          <div>
            Typical price per carat:{" "}
            {formatPricePerCarat(activePercentiles[49])}
          </div>
          <div>
            Percentile 40-60: [{formatPricePerCarat(activePercentiles[39])} -{" "}
            {formatPricePerCarat(activePercentiles[59])}]
          </div>
          <div>
            Percentile 25-75: [{formatPricePerCarat(activePercentiles[24])} -{" "}
            {formatPricePerCarat(activePercentiles[74])}]
          </div>
        </Box>
        {activePoint && (
          <Box
            sx={{
              backgroundColor: "#EEE",
              border: 1,
              padding: "5px",
              margin: "3px",
            }}
          >
            <div>
              <b>{activePoint.source + " " + activePoint.sKU}</b>
            </div>
            <div>
              {activePoint.color} {activePoint.tags ? activePoint.tags : ""}
            </div>
            {/* Shown only when it is not the graph's own color: this stone was priced here
                as an equivalent of it, and that color is otherwise invisible. The hollow
                marker already says "equivalent", so the color stands on its own. */}
            {equivalentColorOf(activePoint, schemeEquivalence) !== null && (
              <div>{equivalentColorOf(activePoint, schemeEquivalence)}</div>
            )}
            <div>
              {formatPricePerCarat(
                isNormalized
                  ? activePoint.perCaratNormalized
                  : activePoint.price / activePoint.carat,
              )}
            </div>
            <div>{activePoint.carat} Carats</div>
          </Box>
        )}
      </div>
    );
  };

  const handleClickZoom = (event: any) => {
    if (!activePoint) {
      if (highlightedPoint) {
        setRangeMax(Math.min(100, Math.max(highlightedPoint.x, 3)));
      } else {
        if (rangeMax < 5) setRangeMax(100);
        else setRangeMax(3);
      }
      setVisibleTooltip(false);
    } else {
      navigate("/analytics/diamondpricing/" + activePoint.id);
    }
  };

  const handleMove = (event: any) => {
    if (event.chartX) {
      const value = Math.round((event.chartX * factor + shift) * 100) / 100;
      setActiveCoord(value);
    }
  };

  const handleOver = (event: any) => {
    const realStartX = event.points[0].payload.x;
    const coordStartX = event.points[0].x;
    const realEndX = event.points[event.points.length - 1].payload.x;
    const coordEndX = event.points[event.points.length - 1].x;

    // We find the factors and shift to map from coord to real

    const factorC = (realEndX - realStartX) / (coordEndX - coordStartX);
    const shiftC = realStartX - coordStartX * factor;

    if (factorC !== factor || shiftC !== shift) {
      setFactor(factorC);
      setShift(shiftC);
    }
    setVisibleTooltip(true);
  };

  const sellableItems = data.diams
    .filter((value: any) => value.isSellable)
    .filter((value: any) => value.clarity === selectedClarity)
    .map((value: any, j: number) => ({
      x: value.carat,
      y: isNormalized ? value.perCaratNormalized : value.price / value.carat,
      raw: value,
    }))
    .filter((value: any) => value.x <= rangeMax);
  const unsellableItems = data.diams
    .filter((value: any) => !value.isSellable)
    .filter((value: any) => value.clarity === selectedClarity)
    .map((value: any, j: number) => ({
      x: value.carat,
      y: isNormalized ? value.perCaratNormalized : value.price / value.carat,
      raw: value,
    }))
    .filter((value: any) => value.x <= rangeMax);
  const highlightedPoint =
    refPoint &&
    data.diams
      .map((value: any, j: number) => ({
        x: value.carat,
        y: isNormalized ? value.perCaratNormalized : value.price / value.carat,
        raw: value,
      }))
      .filter((value: any) => value.x <= rangeMax)
      .find((value: any) => value.raw.id === refPoint);

  const sellableColor = "#1234FF";
  const unsellableColor = "#999999";

  // Diamonds pulled in via an equivalent color (rather than an exact match) render as a
  // hollow marker, keeping the sellable/uncertain fill color (an orthogonal concept).
  const renderDiamondDot = (fillColor: string) => (props: any) => {
    const { cx, cy, payload } = props;
    const isEquivalentColor =
      equivalentColorOf(payload?.raw, schemeEquivalence) !== null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={isEquivalentColor ? "none" : fillColor}
        stroke={fillColor}
        strokeWidth={isEquivalentColor ? 2 : 0}
      />
    );
  };

  const legendPayloadBase = [
    {
      value: pricingColorQuery(sheets[0]) + " " + selectedClarity,
      color: colors[3],
    },
    { value: "Sellable", color: sellableColor },
    {
      value: "Equivalent color",
      color: sellableColor,
      legendIcon: <HollowDotLegendIcon stroke={sellableColor} />,
    },
    { value: "Uncertain", color: unsellableColor },
  ];

  const legendPayload = highlightedPoint
    ? [...legendPayloadBase, { value: "Current", color: "red" }]
    : legendPayloadBase;

  return (
    <ComposedChart
      width={width}
      height={height}
      syncMethod={"value"}
      onClick={(event) => handleClickZoom(event)}
      onMouseMove={(event) => handleMove(event)}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        type="number"
        dataKey="x"
        name="Carat"
        domain={[rangeMin, rangeMax]}
        allowDataOverflow
      />
      <YAxis
        type="number"
        dataKey="y"
        name="Price Per Carat"
        width={120}
        tickFormatter={formatPricePerCarat}
      />
      {percentileDeltas
        .map((delta: number, deltaIdx: number) => (
          <Area
            data={dataPlot}
            dataKey={(data: any) =>
              data?.percentiles && [
                data.percentiles[49 - delta],
                data.percentiles[49 + delta],
              ]
            }
            type="linear"
            stroke={"#0000"}
            fill={colors[deltaIdx]}
            fillOpacity={0.5}
            isAnimationActive={false}
            dot={false}
            activeDot={false}
            onMouseOver={(event) => handleOver(event)}
            onMouseEnter={(event) => setActivePoint(null)}
          />
        ))
        .reverse()}
      {withSamples && (
        <Scatter
          isAnimationActive={false}
          data={sellableItems}
          fill={sellableColor}
          shape={renderDiamondDot(sellableColor)}
          name="Sellable"
          onMouseEnter={(event) => setActivePoint(event?.raw)}
        />
      )}
      {withSamples && (
        <Scatter
          isAnimationActive={false}
          data={unsellableItems}
          fill={unsellableColor}
          shape={renderDiamondDot(unsellableColor)}
          name="Uncertain"
          onMouseEnter={(event) => setActivePoint(event?.raw)}
        />
      )}
      <Tooltip
        content={renderTooltip}
        position={{ x: 150, y: 30 }}
        active={visibleTooltip}
        cursor={false}
        filterNull={false}
      />
      <ReferenceLine
        x={activeCoord}
        stroke={visibleTooltip ? "red" : "#0000"}
      />
      {highlightedPoint && (
        <ReferenceDot
          x={highlightedPoint.x}
          y={highlightedPoint.y}
          stroke={"red"}
          fill="red"
          onMouseEnter={(event) => setActivePoint(highlightedPoint?.raw)}
          r={8}
        />
      )}
      <Legend payload={legendPayload} />
    </ComposedChart>
  );
}

function PricingComputationBase() {
  const token = useAppSelector((state) => state.auth.token);
  const [pricingReport, { loading }] = useMutation(
    ANALYZE_PRICE,
    tokenOptions(token),
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const color = searchParams.get("color");
  // The colour is in the url, so the tab can name it without asking the backend anything.
  useDocumentTitle(color, "Pricing");
  const selectedOptions = !!color ? [{ label: color, value: color }] : [];

  const [selectedClarity, setSelectedClarity] = useHistoryState<string>(
    "selectedUniqueClarity",
    "FL",
  );
  const [data, setData] = useState<any>(null);

  // The colours worth reporting on are their own list, and a cheap one: asking for them does not
  // mean running a report. The analysis itself is only worth its minute once a colour is chosen.
  const { data: colorData } = useQuery(GET_PRICING_COLORS, tokenOptions(token));

  useEffect(() => {
    if (!color) {
      setData(null);
      return;
    }
    pricingReport({ variables: { color } }).then((t) => setData(t?.data));
  }, [pricingReport, color]);

  // Convert data to format expected by react-select
  const options = [...(colorData?.getPricingColors ?? [])]
    .sort(byColorSystemThenName)
    .map((item: string) => ({
      value: item,
      label: item,
    }));

  const handleChange = (selected: any) => {
    if (selected && selected.length > 1) {
      selected = selected.slice(selected.length - 1);
    }
    if (selected && selected.length > 0) {
      if (color !== selected) setData(null);
      setSearchParams({ color: selected.value });
    }
    if (selected) {
      if (color !== selected) setData(null);
      setSearchParams({ color: selected.value });
    }
  };

  const analyze = data?.analyzePrice;
  const equivalentColors = analyze?.prices?.[0]?.equivalentColors;

  return (
    <SectionPaper>
      <PageTitle />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <Stack spacing={2}>
          <Box>
            <Select
              isMulti={false}
              isClearable={false}
              name="colors"
              options={options}
              className="basic-multi-select"
              classNamePrefix="select"
              value={selectedOptions}
              onChange={handleChange}
            />
            {!!color && analyze?.clarities && (
              <Stack direction="column" alignItems="center" sx={{ mt: 1 }}>
                <ToggleButtonGroup
                  exclusive
                  sx={toggleSelectorSx}
                  value={selectedClarity}
                  // A click on the option already chosen arrives as null; keeping one clarity
                  // selected at all times is the point of the control, so it is ignored.
                  onChange={(_, clarity) =>
                    clarity !== null && setSelectedClarity(clarity)
                  }
                  aria-label="Clarity selector"
                >
                  {analyze.clarities.map((clarity: string | null) => (
                    <ToggleButton
                      key={clarity}
                      // A ToggleButton's value cannot be null, where the Button this replaced took
                      // its answer from the label's text — an unnamed clarity read as "" there too.
                      value={clarity ?? ""}
                      // Nothing chosen yet means every clarity is still in play, so they all read as
                      // selected. Stated here rather than left to the group, which would light up
                      // only the one matching its value.
                      selected={
                        clarity === selectedClarity || selectedClarity === null
                      }
                    >
                      {clarity}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
          </Box>

          {!!color && <EquivalenceAlert equivalentColors={equivalentColors} />}

          {!!color && analyze?.prices?.length >= 1 && (
            <Box sx={{ height: "60vh" }}>
              <AutoSizer>
                {(size: Size) => (
                  <SheetGraphPercentile
                    refPoint={null}
                    show={selectedOptions}
                    isNormalized={true}
                    withSamples={true}
                    selectedClarity={selectedClarity}
                    data={analyze}
                    width={size.width}
                    height={size.height}
                  />
                )}
              </AutoSizer>
            </Box>
          )}

          {!!color && analyze?.sources && (
            <Box>
              <SectionTitle>Comparison to Langerman</SectionTitle>
              {String(analyze.sources).replace(
                /^\s*Comparison to Langerman:?\s*/i,
                "",
              )}
            </Box>
          )}

          {!!color &&
            analyze?.colorSpreads &&
            // no equivalent colors means the average across every stone, which is priced from no
            // color system and so falls into no system's spread either
            !!equivalentColors &&
            prepareColorSpreads(analyze.colorSpreads)
              // the queried color's own system is what the graph above already shows, so the
              // spreads worth a section here are the other systems the same stones fall into
              .filter((cs) => cs.system !== equivalentColors?.system)
              .map((cs) => (
                <Box key={cs.system}>
                  <SectionTitle>{COLOR_SYSTEM_LABELS[cs.system]}</SectionTitle>
                  <ColorSpread
                    hideHeader
                    hideIfSingleCategory={false}
                    allowedColorSystems={[cs.system]}
                    colorSpreads={analyze.colorSpreads}
                  />
                </Box>
              ))}
        </Stack>
      )}
    </SectionPaper>
  );
}

const PricingComputation = () => {
  return <PricingComputationBase />;
};

export default PricingComputation;
